import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { MessageSquare, Clock, Shield, RefreshCw, UserCog } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { createAuthData, logAuthEvent } from '@/config/authConfig';

interface SmsAuthProps {
  onAuthSuccess: () => void;
}

interface EmployeeValidation {
  is_valid: boolean;
  employee_data: {
    employee_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    is_admin: boolean;
    role: string;
  };
}

export const SmsAuth = ({ onAuthSuccess }: SmsAuthProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [codeExpiry, setCodeExpiry] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [adminPhoneNumber, setAdminPhoneNumber] = useState<string | null>(null);

  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Extract only digits from formatted phone number
  const extractDigits = (formattedPhone: string) => {
    return formattedPhone.replace(/\D/g, '');
  };

  // Handle phone number change with formatting
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  // Handle Enter key for phone number input
  const handlePhoneNumberKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && phoneNumber.trim()) {
      sendSmsCode();
    }
  };

  // Handle Enter key for SMS code input
  const handleSmsCodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && smsCode.length === 6) {
      verifySmsCode();
    }
  };
  
  const { toast } = useToast();

  const validateEmployee = async (phone: string): Promise<EmployeeValidation | null> => {
    try {
      const { data, error } = await supabase.rpc('validate_employee_phone', {
        input_phone: phone
      });

      if (error) {
        console.error('Employee validation error:', error);
        return null;
      }

      const result = data?.[0] as EmployeeValidation;
      return result || null;
    } catch (error) {
      console.error('Error validating employee:', error);
      return null;
    }
  };

  // Check if user already has valid SMS auth and load admin phone number
  useEffect(() => {
    const staffAuth = localStorage.getItem('staff_sms_auth');
    if (staffAuth) {
      try {
        const authData = JSON.parse(staffAuth);
        const now = new Date().getTime();
        if (authData.authenticated && authData.expiry > now) {
          console.log('Valid SMS auth found, auto-granting access');
          onAuthSuccess();
          return;
        } else {
          console.log('Expired SMS auth found, removing');
          localStorage.removeItem('staff_sms_auth');
        }
      } catch (error) {
        console.error('Error checking existing SMS auth:', error);
        localStorage.removeItem('staff_sms_auth');
      }
    }

    // Load admin phone number setting
    const loadAdminPhoneNumber = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('setting_value')
          .eq('setting_key', 'admin_phone_number')
          .single();
        
        if (!error && data?.setting_value) {
          setAdminPhoneNumber(data.setting_value as string);
        }
      } catch (error) {
        console.error('Error loading admin phone number:', error);
      }
    };
    
    loadAdminPhoneNumber();
  }, [onAuthSuccess]);

  const sendSmsCode = async () => {
    if (!phoneNumber.trim()) {
      toast({
        title: "Phone Number Required",
        description: "Please enter your phone number to receive SMS code.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Format phone number to E.164 format for Supabase
      const cleanNumber = extractDigits(phoneNumber);
      const formattedPhone = cleanNumber.startsWith('1') ? `+${cleanNumber}` : `+1${cleanNumber}`;
      
      console.log('Validating employee phone:', formattedPhone);
      
      // First, validate that this phone number belongs to an active employee
      const employeeValidation = await validateEmployee(formattedPhone);
      
      if (!employeeValidation || !employeeValidation.is_valid) {
        toast({
          title: "Access Denied",
          description: "This phone number is not registered for employee access. Please contact your administrator.",
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      console.log('Employee validated:', employeeValidation.employee_data);
      console.log('Sending OTP to:', formattedPhone);
      
      // Use Supabase's native phone authentication
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone
      });

      if (error) {
        console.error('Supabase OTP error:', error);
        throw new Error(error.message || 'Failed to send SMS verification code');
      }
      
      // Set local state for UI
      const expiry = new Date().getTime() + (10 * 60 * 1000); // 10 minutes
      setCodeExpiry(expiry);
      setCodeSent(true);
      
      toast({
        title: "Code Sent",
        description: `Verification code sent to ${employeeValidation.employee_data.first_name} ${employeeValidation.employee_data.last_name}`,
        variant: "default"
      });

    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send verification code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifySmsCode = async () => {
    console.log('Verifying SMS code:', smsCode);
    
    try {
      // Format phone number to E.164 format for Supabase
      const cleanNumber = extractDigits(phoneNumber);
      const formattedPhone = cleanNumber.startsWith('1') ? `+${cleanNumber}` : `+1${cleanNumber}`;
      
      // Get employee data for session storage
      const employeeValidation = await validateEmployee(formattedPhone);
      
      if (!employeeValidation || !employeeValidation.is_valid) {
        toast({
          title: "Access Denied",
          description: "This phone number is not registered for employee access.",
          variant: "destructive"
        });
        return;
      }
      
      // Use Supabase's native OTP verification
      const { error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: smsCode,
        type: 'sms'
      });

      if (error) {
        console.error('OTP verification error:', error);
        toast({
          title: "Invalid Code",
          description: "The SMS code is invalid or has expired. Please request a new one.",
          variant: "destructive"
        });
        return;
      }

      console.log('SMS code verified successfully');
      const employeeData = employeeValidation.employee_data;
      
      // SECURITY FIX: SMS authentication NEVER grants admin privileges
      // All SMS users get staff-level access only, regardless of their employee role
      const staffSessionData = {
        authenticated: true,
        phoneNumber: formattedPhone,
        expiry: Date.now() + (4 * 60 * 60 * 1000), // 4 hour session
        userName: `${employeeData.first_name} ${employeeData.last_name}`,
        employeeId: employeeData.employee_id,
        role: 'staff', // Force all SMS auth to staff level
        isAdmin: false, // NEVER allow admin via SMS
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('staff_sms_auth', JSON.stringify(staffSessionData));
      
      logAuthEvent('SMS staff authentication created', {
        phoneNumber: formattedPhone,
        actualEmployeeRole: employeeData.role,
        grantedRole: 'staff', // Always staff for SMS
        isAdmin: false,
        expiry: new Date(staffSessionData.expiry).toISOString()
      });
      
      toast({
        title: "Welcome!",
        description: `Signed in as ${employeeData.first_name} ${employeeData.last_name} (Staff Access)`,
      });
      
      setCodeExpiry(null);
      
      console.log('SMS auth granted for:', employeeData.first_name, employeeData.last_name, `(${employeeData.role})`);
      
      onAuthSuccess();
    } catch (error) {
      console.error('Error verifying SMS code:', error);
      toast({
        title: "Verification Error",
        description: "Failed to verify code. Please try again.",
        variant: "destructive"
      });
    }
  };

  const resetFlow = () => {
    setCodeSent(false);
    setSmsCode('');
    setCodeExpiry(null);
  };

  // Timer for code expiry
  useEffect(() => {
    if (!codeExpiry) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const remaining = codeExpiry - now;
      
      if (remaining <= 0) {
        setTimeRemaining("");
        setCodeSent(false);
        setCodeExpiry(null);
      } else {
        const minutes = Math.floor(remaining / (1000 * 60));
        const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [codeExpiry]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <MessageSquare className="w-16 h-16 text-primary" />
        </div>
        <CardTitle className="text-2xl text-foreground mb-2">Staff Login</CardTitle>
        <p className="text-sm text-muted-foreground">Quick SMS Authentication</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">
            {codeSent 
              ? "Enter the verification code sent to your phone"
              : "Enter your phone number to receive a verification code"
            }
          </p>
        </div>

        {!codeSent ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Phone Number:</label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onKeyDown={handlePhoneNumberKeyDown}
                placeholder="XXX-XXX-XXXX"
                className="text-center"
              />
            </div>
            <Button 
              onClick={sendSmsCode} 
              className="w-full" 
              disabled={isLoading || !phoneNumber.trim()}
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Verification Code
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {timeRemaining && (
              <div className="bg-muted border border-border rounded-lg p-3">
                <div className="flex items-center justify-center space-x-2 text-sm text-primary">
                  <Clock className="w-3 h-3" />
                  <span>Code expires in: {timeRemaining}</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <label className="block text-sm font-medium text-center">Enter Verification Code:</label>
              <div className="flex justify-center">
                <div onKeyDown={handleSmsCodeKeyDown}>
                  <InputOTP
                    maxLength={6}
                    value={smsCode}
                    onChange={(value) => setSmsCode(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <Button 
                onClick={verifySmsCode} 
                className="w-full"
                disabled={smsCode.length !== 6}
              >
                <Shield className="w-4 h-4 mr-2" />
                Verify Code & Access System
              </Button>
            </div>

            <div className="text-center">
              <Button variant="ghost" onClick={resetFlow}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Different Number
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    
    {/* Enhanced admin login button */}
    <div className="fixed bottom-6 left-6">
      <Link to="/auth">
        <Button 
          variant="secondary" 
          size="sm" 
          className="shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
        >
          <UserCog className="w-4 h-4" />
          Admin Login
        </Button>
      </Link>
    </div>
    </div>
  );
};
